import { useMemo } from "react";
import { useT } from "@sablier/v2-locales";
import { _ } from "@sablier/v2-mixins";
import { Stream } from "@sablier/v2-models";
import { Summary as Base } from "~/client/components/organisms";
import extensions from "~/client/extensions";
import { useAccount, useCreateSingleForm } from "~/client/hooks";
import { IExtensionDependencies } from "~/client/types";
import type { ComponentProps } from "react";

function Summary() {
  const { address: sender, chainId, isConfigured, signer } = useAccount();
  const fields = useCreateSingleForm((state) => state.fields);
  const { t } = useT();

  const isComplete = useMemo(() => {
    if (
      _.isNil(fields.token.value) ||
      _.isNilOrEmptyString(fields.address.value) ||
      _.isNilOrEmptyString(fields.amount.value)
    ) {
      return false;
    }
    if (fields.duration.isActive) {
      if (_.isNilOrEmptyString(fields.duration.value)) {
        return false;
      }
    } else {
      if (
        _.isNilOrEmptyString(fields.end.value) ||
        _.isNilOrEmptyString(fields.start.value)
      ) {
        return false;
      }
    }

    return true;
  }, [fields]);

  const isValid = useMemo(() => {
    if (
      !_.isNilOrEmptyString(fields.token.warning) ||
      !_.isNilOrEmptyString(fields.address.warning) ||
      !_.isNilOrEmptyString(fields.amount.warning)
    ) {
      return false;
    }
    if (fields.duration.isActive) {
      if (!_.isNilOrEmptyString(fields.duration.warning)) {
        return false;
      }
    } else {
      if (
        !_.isNilOrEmptyString(fields.end.warning) ||
        !_.isNilOrEmptyString(fields.start.warning)
      ) {
        return false;
      }
    }

    return true;
  }, [fields]);

  const items: ComponentProps<typeof Base.Single>["items"] = useMemo(() => {
    if (!isConfigured) {
      return [];
    }

    const timing = fields.duration.isActive
      ? ("duration" as const)
      : ("range" as const);

    const dependencies = {
      amount: fields.amount.value,
      address: fields.address.value,
      cancelability: fields.cancelability.value,
      chainId: chainId!,
      duration: _.isNilOrEmptyString(fields.duration.warning)
        ? fields.duration.value
        : undefined,
      end: _.isNilOrEmptyString(fields.end.warning)
        ? fields.end.value
        : undefined,
      timing,
      token: fields.token.value,
      sender,
      start: _.isNilOrEmptyString(fields.start.warning)
        ? fields.start.value
        : undefined,
      proxy: "",
      signer: signer!,
    } satisfies IExtensionDependencies<"single">;

    return [
      {
        extension: extensions.summary(dependencies, t, fields.extension),
        isComplete,
        isValid,
        list: [
          {
            purpose: "text",
            label: `${_.capitalize(t("words.to"))}:`,
            value: _.isNilOrEmptyString(fields.address.warning)
              ? fields.address.resolution?.preview
              : undefined,
          },
          {
            purpose: timing,
            duration: _.isNilOrEmptyString(fields.duration.warning)
              ? fields.duration.value
              : undefined,
            start: _.isNilOrEmptyString(fields.start.warning)
              ? fields.start.value
              : undefined,
            end: _.isNilOrEmptyString(fields.end.warning)
              ? fields.end.value
              : undefined,
          },
          {
            purpose: "text",
            label: `${t("words.cancelability")}:`,
            value: fields.cancelability.value
              ? t("structs.willBeCancelable")
              : t("structs.willNotBeCancelable"),
          },
        ],
        purpose: "single",
        title: t(Stream.findShape(fields.shape.value).label),
        total: {
          token: fields.token.value,
          value: fields.amount.value,
          isExpected: false,
        },
      },
    ];
  }, [fields, chainId, isComplete, isConfigured, isValid, sender, t, signer]);

  return <Base.Single items={items} />;
}

export default Summary;
